import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Heart, Star, Play, MessageCircle, Flag, Trophy, Send, Users, Film, Image as ImageIcon, X, CheckCircle2, Boxes, Shuffle, Globe2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import GameCanvas from "@/components/GameCanvas";
import LiveCreatorBar from "@/components/LiveCreatorBar";
import CreatorTierBadge from "@/components/CreatorTierBadge";
import Campaign from "@/components/Campaign";
import GamePasses from "@/components/GamePasses";
import ShareScoreCard from "@/components/ShareScoreCard";
import ShareGameCard from "@/components/ShareGameCard";
import ShareClipButton from "@/components/ShareClipButton";
import AutoPlaytestButton from "@/components/AutoPlaytestButton";
import CommunityClips from "@/components/CommunityClips";
import SandboxShare from "@/components/SandboxShare";
import CinematicMode from "@/components/CinematicMode";
import TipJar from "@/components/TipJar";
import SupportGameCard from "@/components/SupportGameCard";
import CheerWall from "@/components/CheerWall";
import DareTauntWall from "@/components/DareTauntWall";
import GameSaves from "@/components/GameSaves";
import GameMusic from "@/components/GameMusic";
import { LicenceProvenanceCard } from "@/components/LicenceProvenanceCard";
import { Cast3DDialog } from "@/components/Cast3DViewer";
import { GameCard, Spinner } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
const fmtSec = (s) => { if (s == null) return "—"; const m = Math.floor(s / 60); const r = (s % 60).toFixed(1); return m > 0 ? `${m}:${r < 10 ? "0" : ""}${r}` : `${r}s`; };
const fmtStat = (v, fmt) => {
  if (v == null) return "—";
  if (fmt === "time") return fmtSec(v);
  if (fmt === "wave") return `Wave ${Math.round(v)}`;
  return Math.round(v);
};
const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const RARITY_HEX = { common: "#9aa4b2", uncommon: "#5bd06e", rare: "#4aa3ff", epic: "#b06bff", legendary: "#f5b301", mythic: "#ff5470" };

export default function GamePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [game, setGame] = useState(null);
  const [config, setConfig] = useState(null);
  const [livePreview, setLivePreview] = useState(null);   // Live Creator sandbox (un-saved preview)
  const [robotScore, setRobotScore] = useState(null);      // latest robot playtest score (for Share The Score)
  const [comment, setComment] = useState("");
  const [lastScore, setLastScore] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [castModel, setCastModel] = useState(null);
  const canvasRef = useRef(null);
  const [finishSignal, setFinishSignal] = useState(0);
  const [searchParams] = useSearchParams();
  const autoBuildId = searchParams.get("build");
  const loadoutCode = searchParams.get("loadout");
  const challengeCode = searchParams.get("challenge");
  const autoLoadedRef = useRef(false);
  const autoPlayedRef = useRef(false);
  const loadoutAppliedRef = useRef(false);
  const canvasReadyRef = useRef(false);
  const pendingLoadoutRef = useRef(null);
  const [featuredLoadouts, setFeaturedLoadouts] = useState([]);
  const [dare, setDare] = useState(null);
  const [dareOpen, setDareOpen] = useState(true);
  const [dareReported, setDareReported] = useState(false);
  const [lastRun, setLastRun] = useState(null);   // {wave, score, loadout} from the last Boss Rush run
  const [beatDareName, setBeatDareName] = useState("");   // set when the last run beat an incoming dare
  const [event, setEvent] = useState(null);       // active seasonal event (tints weather + boss)
  const [worldRecap, setWorldRecap] = useState(null);   // "While you were away…" Living World recap

  useEffect(() => {
    api.get("/events/active").then((r) => { if (r.data?.active) setEvent(r.data); }).catch(() => {});
  }, []);

  // Incoming dare (?challenge=<code>): show the "beat my Wave N" banner + gear up with their build.
  useEffect(() => {
    if (!challengeCode) return;
    api.get(`/dares/${challengeCode}`).then((r) => setDare(r.data)).catch(() => {});
  }, [challengeCode]);

  const loadFeatured = useCallback(() => {
    api.get(`/loadouts/game/${id}`).then((r) => setFeaturedLoadouts(r.data || [])).catch(() => {});
  }, [id]);
  useEffect(() => { loadFeatured(); }, [loadFeatured]);

  // Equip a loadout snapshot — applies now if the game is running, else starts it and applies on ready.
  const equipLoadoutData = useCallback((data) => {
    if (!data) return;
    if (canvasReadyRef.current) {
      setTimeout(() => { canvasRef.current?.applyLoadout?.(data); toast.success("Loadout equipped — gear on!"); }, 300);
    } else {
      pendingLoadoutRef.current = data;
      if (!config) playNow();
    }
    /* eslint-disable-next-line */
  }, [config]);

  const importFeatured = useCallback(async (code) => {
    try { const { data } = await api.get(`/loadouts/${code}`); equipLoadoutData(data?.data); }
    catch { toast.error("Couldn't load that starter loadout."); }
  }, [equipLoadoutData]);

  // When arriving from the "creation of the week" spotlight (?build=<id>), auto-load
  // that community build into the running sandbox once the game canvas is ready.
  const handleCanvasReady = useCallback(() => {
    canvasReadyRef.current = true;
    // Apply a loadout that was queued before the canvas was ready (gallery click).
    if (pendingLoadoutRef.current) {
      const d = pendingLoadoutRef.current; pendingLoadoutRef.current = null;
      setTimeout(() => { canvasRef.current?.applyLoadout?.(d); toast.success("Loadout equipped — gear on!"); }, 1300);
    }
    // Import a shared gear loadout (?loadout=<code>) — or the loadout attached to an incoming dare.
    const importCode = loadoutCode || dare?.loadout_code;
    if (importCode && !loadoutAppliedRef.current) {
      loadoutAppliedRef.current = true;
      api.get(`/loadouts/${importCode}`).then((r) => {
        const data = r.data?.data;
        if (data) setTimeout(() => {
          canvasRef.current?.applyLoadout?.(data);
          toast.success(dare ? "Geared up with your challenger's build — go beat their score!" : "Imported a shared loadout — your gear is equipped!");
        }, 1300);
      }).catch(() => { if (loadoutCode) toast.error("That shared loadout link is invalid or expired."); });
    }
    if (!autoBuildId || autoLoadedRef.current) return;
    autoLoadedRef.current = true;
    api.get(`/sandbox/builds/${autoBuildId}`).then((r) => {
      const blocks = r.data?.blocks || [];
      if (!blocks.length) return;
      setTimeout(() => {
        canvasRef.current?.loadSandbox?.(blocks);
        toast.success(`Loaded "${r.data?.name || "this creation"}" — explore it, then remix & re-share!`);
      }, 1000);
    }).catch(() => {});
  }, [autoBuildId, loadoutCode, dare]);

  // Share the player's current gear loadout → short link others can import. Owners can also
  // FEATURE it (named) on the game's page for newcomers.
  const handleShareLoadout = useCallback(async (loadout) => {
    let name = "", featured = false;
    if (game?.is_owner) {
      const n = window.prompt("Feature this loadout on your game page for newcomers?\nEnter a name to feature it (or leave blank to just copy a private share link):", "");
      if (n && n.trim()) { name = n.trim().slice(0, 40); featured = true; }
    }
    try {
      const { data } = await api.post("/loadouts", { game_id: id, data: loadout, name, featured });
      const link = `${window.location.origin}/app/arcade/${id}?loadout=${data.code}`;
      try { await navigator.clipboard?.writeText(link); } catch {}
      toast.success(data.featured ? "Featured on your game page + link copied!" : "Loadout link copied — share it so friends can import your build!");
      if (data.featured) loadFeatured();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the share link.");
    }
  }, [id, game, loadFeatured]);

  // Auto-save the player's progress to the cloud "auto" slot when they pause the game.
  const handleAutosave = useCallback(async () => {
    if (!user) return;
    const st = await canvasRef.current?.saveState?.();
    if (st) api.post(`/games/${id}/progress`, { slot: "auto", state: st }).catch(() => {});
  }, [user, id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/arcade/${id}`);
      setGame(data);
    } catch { toast.error("Game not found"); navigate("/app/arcade"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const playNow = async () => {
    try {
      const { data } = await api.post(`/arcade/${id}/play`);
      const cfg = data.config || {};
      // Seasonal World Event — tint the launched game's weather + boss with the live season.
      if (event && (cfg.dimension === "3d")) {
        if (event.weather && event.weather !== "clear") {
          cfg.world = { ...(cfg.world || {}), weather: event.weather };
        }
        if (event.boss_tint) cfg.__eventBossTint = event.boss_tint;
        if (event.accent) cfg.__eventLootGlow = event.accent;
        if (event.loot && event.loot.length) cfg.__eventLoot = event.loot;
        if (event.music) cfg.__eventMusic = event.music;
        // Per-event signature boss — only reskin games that already have a boss.
        if (event.boss && cfg.boss && (cfg.boss.name || cfg.boss.emoji || cfg.boss.image)) {
          cfg.boss = { ...cfg.boss, name: event.boss.name, emoji: event.boss.emoji, image: null };
        }
      }
      setConfig(cfg);
      api.post("/quests/track", { event: "play" }).catch(() => {});
      // Living World — fetch this player's world state (NPC routines + memory + recap),
      // attach it for the runtime, and surface a "While you were away…" welcome-back card.
      if (cfg.dimension === "3d") {
        api.get(`/living-world/${id}/state`).then((r) => {
          const lw = r.data;
          if (lw?.exists && (lw.npcs || []).length) {
            setConfig((c) => (c ? { ...c, livingWorld: lw } : c));
            if (lw.returning && (lw.recap || []).length) setWorldRecap(lw);
          }
        }).catch(() => {});
      }
    } catch { toast.error("Could not start game"); }
  };

  // A Living World NPC was greeted in-game → persist the visit + deepen the relationship.
  const onNpcInteract = useCallback(async (d) => {
    if (!d?.npc_id) return;
    try {
      const { data } = await api.post(`/living-world/${id}/interact`, { npc_id: d.npc_id, type: d.interaction || "greet" });
      if (data?.cooldown) { if (data.message) toast.message(data.message); return; }
      if (data?.leveled_up) toast.success(`💚 ${data.name} now sees you as a ${data.level}!`);
      else if (data?.message) toast.success(data.message);
    } catch {}
  }, [id]);

  // Arriving via the spotlight (?build=<id>) or a shared loadout (?loadout=<code>): auto-start
  // the game so the build/loadout loads without an extra "Play Now" click.
  useEffect(() => {
    if ((autoBuildId || loadoutCode || challengeCode) && game && !config && !autoPlayedRef.current) {
      autoPlayedRef.current = true;
      playNow();
    }
    /* eslint-disable-next-line */
  }, [autoBuildId, loadoutCode, challengeCode, game, config]);

  const like = async () => {
    try {
      const { data } = await api.post(`/arcade/${id}/like`);
      setGame((g) => ({ ...g, liked: data.liked, likes: data.likes }));
      if (data.liked) api.post("/quests/track", { event: "like" }).catch(() => {});
    } catch {}
  };

  const rate = async (stars) => {
    try {
      const { data } = await api.post(`/arcade/${id}/rate`, { rating: stars });
      setGame((g) => ({ ...g, rating: data.rating, rating_count: data.rating_count, your_rating: stars }));
      toast.success("Thanks for rating!");
    } catch {}
  };

  const onGameOver = async (d) => {
    setLastScore(d.score);
    setFinishSignal((n) => n + 1);
    try {
      const { data } = await api.post(`/arcade/${id}/score`, { score: d.score });
      setGame((g) => ({ ...g, leaderboard: data.leaderboard }));
    } catch {}
    if (d.metrics && d.metrics.metric) {
      try {
        const { data } = await api.post(`/arcade/${id}/stat`, {
          metric: d.metrics.metric, value: d.metrics.value, extra: d.metrics.extra ?? undefined,
        });
        setGame((g) => ({ ...g, stat_board: data.stat_board }));
      } catch {}
    }
    if (d.metrics && d.metrics.place) {
      try { await api.post(`/racing/record`, { game_id: id, place: d.metrics.place, total: d.metrics.total || 4 }); } catch {}
    }
    // Boss Rush run finished — remember it so the player can dare a friend, and settle any incoming dare.
    if (d.metrics && d.metrics.metric === "wave") {
      const wave = Number(d.metrics.value) || 0;
      setLastRun({ wave, score: d.score, loadout: d.loadout || null });
      if (dare && dare.wave != null) {
        if (wave > dare.wave) {
          toast.success(`🏆 You beat ${dare.challenger_name}'s Wave ${dare.wave}! Now dare them back.`);
          setBeatDareName(dare.challenger_name || "them");
          try {
            const { data: br } = await api.post(`/dares/${dare.code}/beat`);
            if (br?.first && br.reward > 0) {
              const streakTxt = br.streak > 1 ? `🔥 ${br.streak}-dare streak! ` : "🎁 First to topple this dare — ";
              setTimeout(() => toast.success(`${streakTxt}+${br.reward} bonus AI credits!`), 900);
              refresh?.();
            }
          } catch {}
        } else {
          setBeatDareName("");
          toast(`So close — ${dare.challenger_name} survived Wave ${dare.wave}. You hit Wave ${wave}. Try again!`);
        }
      } else {
        setBeatDareName("");
      }
    }
  };

  // Flag a bad/impossible dare so the Hall of Dares stays fair.
  const reportDare = useCallback(async () => {
    if (!dare) return;
    setDareReported(true);
    try { await api.post(`/dares/${dare.code}/report`); toast.success("Thanks — we'll review this dare."); }
    catch { toast.error("Couldn't report this dare."); }
  }, [dare]);

  // Turn the last Boss Rush run into a one-tap dare (wave score + the exact loadout you used).
  const challengeAFriend = useCallback(async () => {
    if (!lastRun) return;
    try {
      let loadout_code = "";
      if (lastRun.loadout && Object.keys(lastRun.loadout).length) {
        try {
          const { data: ld } = await api.post("/loadouts", { game_id: id, data: lastRun.loadout });
          loadout_code = ld.code;
        } catch { /* dare still works without a loadout */ }
      }
      const { data } = await api.post("/dares", { game_id: id, wave: lastRun.wave, score: lastRun.score, loadout_code });
      const link = `${window.location.origin}/app/arcade/${id}?challenge=${data.code}`;
      try { await navigator.clipboard?.writeText(link); } catch {}
      toast.success(`Dare link copied — challenge a friend to beat Wave ${lastRun.wave}!`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the dare link.");
    }
  }, [lastRun, id]);

  const postComment = async () => {
    if (!comment.trim()) return;
    try {
      const { data } = await api.post(`/arcade/${id}/comments`, { text: comment });
      setGame((g) => ({ ...g, comments: [data, ...(g.comments || [])] }));
      setComment("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setComment(""); }
  };

  const report = async () => {
    const reason = window.prompt("Why are you reporting this game?");
    if (!reason) return;
    await api.post("/reports", { target_type: "game", target_id: id, reason });
    toast.success("Thanks for helping keep GameView safe.");
  };

  if (!game) return <Spinner />;

  const sp = game.store_page || {};
  const trailer = ytEmbed(sp.trailer_url);
  const trailerClip = sp.trailer_clip_url;
  const shots = sp.screenshots || [];

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto">
      <Button variant="ghost" size="sm" onClick={() => navigate("/app/arcade")} className="mb-4" data-testid="gp-back"><ArrowLeft className="w-4 h-4" /> Arcade</Button>

      {/* Steam-like hero */}
      <div className="relative rounded-2xl overflow-hidden border border-[hsl(var(--border))] mb-6" data-testid="gp-hero">
        <div className="absolute inset-0" style={{ background: game.cover_image
          ? `center/cover no-repeat url(${imgUrl(game.cover_image)})`
          : `linear-gradient(135deg, ${game.world?.bgColor || "#0b1026"}, ${game.world?.accent || "#1f6feb"})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />
        <div className="relative p-6 lg:p-8 flex flex-col justify-end min-h-[220px]">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className="bg-white/15 backdrop-blur text-white border-0">{game.category}</Badge>
            <Badge className="bg-white/15 backdrop-blur text-white border-0">Ages {game.age_rating}</Badge>
            {game.has_levels && game.levels_meta?.count > 0 && <Badge className="bg-[hsl(var(--primary))] text-white border-0">{game.levels_meta.count}-level campaign</Badge>}
            {game.robot_certified && <Badge className="bg-violet-500 text-white border-0" data-testid="gp-certified-badge" title={`Robot-certified fun — scored ${game.robot_best}/100`}>🤖 Certified Fun {game.robot_best}</Badge>}
          </div>
          <h1 className="font-display text-3xl lg:text-5xl font-extrabold text-white drop-shadow-lg flex items-center gap-3">{game.cover_emoji} {game.title}</h1>
          {sp.headline && <p className="text-white/90 text-base lg:text-lg mt-2 max-w-2xl drop-shadow">{sp.headline}</p>}
          {sp.tagline && <p className="text-white/70 text-sm mt-1 max-w-2xl">{sp.tagline}</p>}
          <button onClick={() => game.owner_id && navigate(`/app/creators/${game.owner_id}`)} className="text-white/70 text-sm mt-2 hover:underline w-fit flex items-center gap-2" data-testid="gp-creator-link">by {game.owner_name}
            {game.owner_tier && <CreatorTierBadge tier={game.owner_tier} />}
          </button>
          {game.remixed_from && (
            <button onClick={() => navigate(`/app/arcade/${game.remixed_from.game_id}`)} className="text-white/60 text-xs mt-1.5 hover:text-white hover:underline w-fit flex items-center gap-1.5" data-testid="gp-remixed-from">
              <Shuffle className="w-3.5 h-3.5" /> Remixed from “{game.remixed_from.title}”{game.remixed_from.owner_name ? ` by ${game.remixed_from.owner_name}` : ""}
            </button>
          )}
          <div className="mt-3" data-testid="gp-hero-cinematic">
            <CinematicMode gameId={id} game={game} />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {dare && dareOpen && (
            <div className="mb-4 rounded-2xl border border-[#f5b301]/50 bg-gradient-to-r from-[#f5b301]/15 to-[#ff7a59]/10 p-4 flex items-center gap-3" data-testid="gp-dare-banner">
              <span className="shrink-0 text-3xl">🔥</span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm sm:text-base">
                  {dare.challenger_name} dares you to beat <span className="text-[hsl(var(--primary))]">Wave {dare.wave}</span>!
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                  {dare.message ? `“${dare.message}” · ` : ""}
                  {dare.loadout_code ? "Their exact loadout is equipped for you — " : ""}survive longer to win.
                  {dare.beaten > 0 ? ` Beaten ${dare.beaten}×.` : ""}
                </p>
              </div>
              {!config && (
                <Button size="sm" onClick={playNow} data-testid="gp-dare-accept" className="rounded-full shrink-0">
                  <Play className="w-4 h-4" /> Accept
                </Button>
              )}
              <button onClick={reportDare} disabled={dareReported} data-testid="gp-dare-report" title="Report this dare" className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-red-500 disabled:opacity-40"><Flag className="w-4 h-4" /></button>
              <button onClick={() => setDareOpen(false)} className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="gp-dare-dismiss"><X className="w-4 h-4" /></button>
            </div>
          )}
          {dare && <DareTauntWall dare={dare} user={user} event={event} />}
          {game.has_levels ? (
            <Campaign gameId={id} game={game} onLeaderboard={(lb) => setGame((g) => ({ ...g, leaderboard: lb }))} onPurchased={load} />
          ) : config ? (
            <>
              <div className="relative">
                <GameCanvas ref={canvasRef} config={config} previewConfig={livePreview} onGameOver={onGameOver} onReady={handleCanvasReady} onAutosave={handleAutosave} onShareLoadout={handleShareLoadout} onNpcInteract={onNpcInteract} />
                {user && game.owner_id === user.id && !["quiz", "memory", "visualnovel"].includes(config.template) && (
                  <LiveCreatorBar
                    gameId={id}
                    config={livePreview || config}
                    canvasRef={canvasRef}
                    is3d={config.dimension === "3d"}
                    onPreview={setLivePreview}
                    onClear={() => setLivePreview(null)}
                    onAccept={(cfg) => { setConfig(cfg); setLivePreview(null); }}
                  />
                )}
              </div>
              <div className="flex items-center justify-center gap-2 mt-3" data-testid="gp-saves-bar">
                <GameSaves gameId={id} canvasRef={canvasRef} user={user} template={config.template} />
                <GameMusic gameId={id} game={game} user={user} onConfig={setConfig} />
              </div>
              {!["quiz", "memory"].includes(config.template) && (
                <div className="flex items-center justify-center gap-2 mt-3 flex-wrap" data-testid="gp-clip-bar">
                  <ShareClipButton canvasRef={canvasRef} game={game} gameId={id} user={user} finishSignal={finishSignal} robotScore={robotScore} />
                  {config.dimension === "3d" && (
                    <AutoPlaytestButton canvasRef={canvasRef} gameId={id} canFix={!!(user && game.owner_id === user.id)} autoRun={!!(game?.auto_robot_pending && user && game.owner_id === user.id)} onFixed={setConfig} onReport={(r) => setRobotScore(r?.score ?? null)} />
                  )}
                </div>
              )}
              {config.template === "sandbox3d" && (
                <SandboxShare canvasRef={canvasRef} gameId={id} user={user} autoRemixBuildId={autoBuildId} />
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
              <div className="h-72 grid place-items-center" style={{ background: `linear-gradient(135deg, ${game.world?.bgColor || "#0b1026"}, ${game.world?.accent || "#1f6feb"})` }}>
                <div className="text-center">
                  <div className="text-7xl mb-4">{game.cover_emoji}</div>
                  <Button size="lg" onClick={playNow} data-testid="gp-play-btn" className="rounded-full px-8 py-6 bg-white text-black hover:bg-white/90">
                    <Play className="w-5 h-5 fill-black" /> Play Now
                  </Button>
                </div>
              </div>
            </div>
          )}
          {(game.has_levels || !config) && (
            <div className="flex items-center justify-center mt-3" data-testid="gp-music-bar">
              <GameMusic gameId={id} game={game} user={user} onConfig={setConfig} />
            </div>
          )}
          {!game.has_levels && lastScore !== null && (
            <div className="flex flex-col items-center gap-2 mt-2" data-testid="gp-score-bar">
              <p className="text-center font-bold text-[hsl(var(--primary))]" data-testid="gp-score">Your score: {lastScore} 🎉</p>
              {lastRun && lastRun.wave > 0 && (
                <Button onClick={challengeAFriend} data-testid="gp-challenge-btn" className="rounded-full bg-gradient-to-r from-[#f5b301] to-[#ff7a59] text-black hover:opacity-90 border-0">
                  {beatDareName
                    ? `🔁 Dare ${beatDareName} back to beat Wave ${lastRun.wave}`
                    : `🔥 Challenge a friend to beat Wave ${lastRun.wave}`}
                </Button>
              )}
            </div>
          )}

          {/* Starter Loadouts gallery — featured gear builds creators pinned for newcomers */}
          {featuredLoadouts.length > 0 && (
            <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gp-loadout-gallery">
              <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Boxes className="w-5 h-5 text-[hsl(var(--primary))]" /> Starter Loadouts</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Jump in gear-ready — tap a build to equip it instantly.</p>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="gp-loadout-list">
                {featuredLoadouts.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => importFeatured(l.code)}
                    data-testid={`gp-loadout-${l.code}`}
                    className="flex items-center gap-3 text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 hover:bg-[hsl(var(--secondary))] p-3 transition-colors card-hover"
                  >
                    <span className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-gradient-to-br from-[#f5b301] to-[#ff7a59] text-lg">🎒</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold truncate">{l.name || "Loadout"}</span>
                      {l.preview && l.preview.length > 0 ? (
                        <span className="flex items-center gap-1 mt-0.5" data-testid={`gp-loadout-preview-${l.code}`}>
                          {l.preview.map((p, i) => (
                            <span
                              key={i}
                              title={p.rarity}
                              className="w-5 h-5 rounded grid place-items-center text-[11px] border"
                              style={{ borderColor: (RARITY_HEX[p.rarity] || RARITY_HEX.common) + "aa", background: (RARITY_HEX[p.rarity] || RARITY_HEX.common) + "22" }}
                            >{p.icon}</span>
                          ))}
                          {l.item_count > l.preview.length && (
                            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">+{l.item_count - l.preview.length}</span>
                          )}
                          {l.coins > 0 && <span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1">🪙{l.coins}</span>}
                        </span>
                      ) : (
                        <span className="block text-xs text-[hsl(var(--muted-foreground))] truncate">by {l.owner_name || game.owner_name} · tap to gear up</span>
                      )}
                    </span>
                    <Play className="w-4 h-4 shrink-0 text-[hsl(var(--primary))]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Community-shared clips of this game */}
          <div className="mt-6">
            <CommunityClips gameId={id} title="🎬 Player Clips" testid="gp-clips" />
          </div>

          {/* Media: trailer + screenshots */}
          {(trailerClip || trailer || shots.length > 0) && (
            <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gp-media">
              <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Film className="w-5 h-5" /> Media</h3>
              {trailerClip ? (
                <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))] aspect-video mb-3 bg-black" data-testid="gp-trailer-clip">
                  <video src={imgUrl(trailerClip)} controls loop playsInline poster={game.cover_image ? imgUrl(game.cover_image) : undefined} className="w-full h-full object-cover" />
                </div>
              ) : trailer && (
                <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))] aspect-video mb-3" data-testid="gp-trailer">
                  <iframe src={trailer} title="trailer" className="w-full h-full" allow="encrypted-media" allowFullScreen />
                </div>
              )}
              {shots.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="gp-screenshots">
                  {shots.map((s, i) => (
                    <button key={s} onClick={() => setLightbox(s)} className="rounded-xl overflow-hidden border border-[hsl(var(--border))] aspect-video card-hover" data-testid={`gp-shot-${i}`}>
                      <img src={imgUrl(s)} alt={`screenshot ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AI-generated 3D cast */}
          {(game.cast_3d || []).length > 0 && (
            <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gp-cast-3d">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display font-bold flex items-center gap-2"><Boxes className="w-5 h-5 text-[hsl(var(--primary))]" /> The 3D Cast</h3>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]" data-testid="gp-cast-badge">
                  <Boxes className="w-3 h-3" /> AI-built · Meshy 3D
                </span>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Real AI-generated 3D characters you'll meet in this game.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gp-cast-grid">
                {game.cast_3d.map((m) => (
                  <button key={m.asset_id} onClick={() => m.glb && setCastModel(m)} disabled={!m.glb} data-testid={`gp-cast-${m.asset_id}`}
                    className={`group text-left rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-black/20 ${m.glb ? "card-hover cursor-pointer" : "cursor-default"}`}>
                    <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-black/30 to-black/10">
                      <img src={imgUrl(m.thumbnail)} alt={m.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      {m.glb && (
                        <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-white px-3 py-1.5 rounded-full bg-[#7c3aed]"><Boxes className="w-4 h-4" /> View in 3D</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-bold truncate" title={m.name}>{m.name}</div>
                      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--primary))] font-semibold">{m.role}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Comments ({game.comments?.length || 0})</h3>
            <div className="flex gap-2 mb-4">
              <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Add a friendly comment…" data-testid="gp-comment-input" />
              <Button size="icon" onClick={postComment} data-testid="gp-comment-send"><Send className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-3">
              {(game.comments || []).map((c) => (
                <div key={c.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full grid place-items-center bg-[hsl(var(--secondary))]">{c.avatar_emoji}</div>
                  <div><div className="text-sm font-bold">{c.user_name}</div><div className="text-sm text-[hsl(var(--muted-foreground))]">{c.text}</div></div>
                </div>
              ))}
              {(!game.comments || game.comments.length === 0) && <p className="text-sm text-[hsl(var(--muted-foreground))]">Be the first to comment!</p>}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h3 className="font-display font-bold mb-2">About this game</h3>
            <p className="text-sm">{game.description}</p>
            {sp.features?.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="gp-features">
                {sp.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> {f}</div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge variant="secondary">{game.category}</Badge>
              <Badge variant="secondary">Ages {game.age_rating}</Badge>
              <Badge variant="secondary">{game.template}</Badge>
              {game.has_levels && game.levels_meta?.count > 0 && (
                <Badge className="bg-[hsl(var(--primary))] text-white" data-testid="gp-campaign-badge">{game.levels_meta.count}-level campaign</Badge>
              )}
              {(game.tags || []).map((t) => <Badge key={t} variant="outline">#{t}</Badge>)}
            </div>
            <div className="flex items-center gap-4 mt-4 text-sm">
              <span className="flex items-center gap-1.5"><Play className="w-4 h-4" /> {game.plays}</span>
              <button onClick={like} data-testid="gp-like-btn" className="flex items-center gap-1.5 font-semibold">
                <Heart className={`w-4 h-4 ${game.liked ? "fill-red-500 text-red-500" : ""}`} /> {game.likes}
              </button>
              <TipJar gameId={id} game={game} />
              {game.remix_count > 0 && (
                <span className="flex items-center gap-1.5 text-[hsl(var(--primary))] font-semibold" data-testid="gp-remix-count" title="Times a creator remixed a character from this game">
                  <Shuffle className="w-4 h-4" /> {game.remix_count}
                </span>
              )}
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-1">Rate this game</div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => rate(s)} data-testid={`gp-rate-${s}`}>
                    <Star className={`w-6 h-6 ${(game.your_rating || 0) >= s ? "fill-amber-400 text-amber-400" : "text-[hsl(var(--muted-foreground))]"}`} />
                  </button>
                ))}
              </div>
              {game.rating > 0 && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{game.rating} ★ ({game.rating_count} ratings)</div>}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/community/${id}`)} className="rounded-full" data-testid="gp-community-btn"><Users className="w-4 h-4" /> Community</Button>
              <ShareScoreCard game={game} gameId={id} user={user} lastScore={lastScore} />
              <ShareGameCard game={game} gameId={id} />
              <Button variant="ghost" size="sm" onClick={report} className="text-[hsl(var(--muted-foreground))]" data-testid="gp-report-btn"><Flag className="w-4 h-4" /> Report</Button>
            </div>
          </div>

          <GamePasses gameId={id} isOwner={game.is_owner} />

          <SupportGameCard gameId={id} game={game} />

          <CheerWall creatorId={game.owner_id} creatorName={game.owner_name} />

          <LicenceProvenanceCard gameId={id} />

          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Leaderboard</h3>
            {(game.leaderboard || []).length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No scores yet — play to set the record!</p> : (
              <div className="space-y-2">
                {game.leaderboard.map((s, i) => (
                  <div key={s.id || i} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><span className="w-5 font-bold">{i + 1}</span>{s.avatar_emoji} {s.user_name}</span>
                    <span className="font-bold">{s.score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {game.stat_board && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gp-stat-board">
              <h3 className="font-display font-bold mb-3 flex items-center gap-2">{game.stat_board.label}</h3>
              {(game.stat_board.entries || []).length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">No records yet — play to claim the top spot!</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                    <span className="pl-7">Player</span>
                    <span className="flex gap-4">
                      <span className="w-16 text-right">{game.stat_board.value_col}</span>
                      {game.stat_board.extra_col && <span className="w-16 text-right">{game.stat_board.extra_col}</span>}
                    </span>
                  </div>
                  {game.stat_board.entries.map((s, i) => (
                    <div key={s.id || i} className="flex items-center justify-between text-sm" data-testid={`gp-stat-row-${i}`}>
                      <span className="flex items-center gap-2"><span className="w-5 font-bold">{["🥇", "🥈", "🥉"][i] || i + 1}</span>{s.avatar_emoji} {s.user_name}</span>
                      <span className="flex gap-4 font-mono">
                        <span className="w-16 text-right font-bold text-[hsl(var(--primary))]">{fmtStat(s.value, game.stat_board.fmt)}</span>
                        {game.stat_board.extra_col && <span className="w-16 text-right text-[hsl(var(--muted-foreground))]">{fmtSec(s.extra)}</span>}
                      </span>
                    </div>
                  ))}
                  {game.stat_board.my_rank && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))] pt-2 border-t border-[hsl(var(--border))]">
                      Your rank: <b className="text-[hsl(var(--foreground))]">#{game.stat_board.my_rank}</b> · {fmtStat(game.stat_board.my_value, game.stat_board.fmt)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {(game.more_by_creator || []).length > 0 && (
            <div>
              <h3 className="font-display font-bold mb-3">More by {game.owner_name}</h3>
              <div className="grid grid-cols-2 gap-3">
                {game.more_by_creator.map((g) => <GameCard key={g.id} game={g} onClick={() => navigate(`/app/arcade/${g.id}`)} />)}
              </div>
            </div>
          )}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 grid place-items-center p-6" onClick={() => setLightbox(null)} data-testid="gp-lightbox">
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white" onClick={() => setLightbox(null)}><X className="w-5 h-5" /></button>
          <img src={imgUrl(lightbox)} alt="screenshot" className="max-w-full max-h-[88vh] rounded-xl cursor-zoom-out" />
        </div>
      )}
      <Cast3DDialog model={castModel} gameId={id} onClose={() => setCastModel(null)} />

      {/* Living World — "While you were away…" welcome-back recap */}
      {worldRecap && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-6" onClick={() => setWorldRecap(null)} data-testid="lw-recap-overlay">
          <div className="relative w-full max-w-md rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-[#0c1a12] to-[#0a0f0c] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 text-white/80 hover:text-white" onClick={() => setWorldRecap(null)} data-testid="lw-recap-close"><X className="w-4 h-4" /></button>
            <div className="flex items-center gap-2 text-emerald-400 mb-1"><Globe2 className="w-5 h-5" /> <span className="text-xs font-bold uppercase tracking-wide">While you were away…</span></div>
            <h3 className="font-display text-xl font-bold text-white mb-3">Welcome back to {game.title}</h3>
            <ul className="space-y-2.5 mb-5" data-testid="lw-recap-list">
              {(worldRecap.recap || []).map((line, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-emerald-50/90" data-testid={`lw-recap-item-${i}`}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" /> {line}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-emerald-200/50 mb-3">Visit #{worldRecap.visit_number} · it's {worldRecap.slot_label?.toLowerCase()} in {game.title}</p>
            <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white border-0" onClick={() => setWorldRecap(null)} data-testid="lw-recap-continue"><Play className="w-4 h-4" /> Step back in</Button>
          </div>
        </div>
      )}
    </div>
  );
}
